
import { Union, Record } from "./fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type, class_type, record_type, option_type, string_type } from "./fable_modules/fable-library-js.5.3.0/Reflection.js";
import { isNullOrWhiteSpace } from "./fable_modules/fable-library-js.5.3.0/String.js";
import { value } from "./fable_modules/fable-library-js.5.3.0/Option.js";

export class ConfirmResult extends Record {
    constructor(Type, Error$) {
        super();
        this.Type = Type;
        this.Error = Error$;
    }
}

export function ConfirmResult_$reflection() {
    return record_type("Stripe.ConfirmResult", [], ConfirmResult, () => [["Type", string_type], ["Error", option_type(string_type)]]);
}

/**
 * What a click on "Confirm Payment" should do, given the checkout state.
 * NotReady carries a user-facing message: a click must never silently do nothing.
 */
export class ConfirmClickAction extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["StartConfirm", "NotReady"];
    }
}

export function ConfirmClickAction_$reflection() {
    return union_type("Stripe.ConfirmClickAction", [], ConfirmClickAction, () => [[["Item", class_type("Stripe.CheckoutInstance")]], [["Item", string_type]]]);
}

/**
 * Decide what a confirm click does based on whether the checkout session is initialised.
 */
export function confirmClickAction(checkout) {
    if (checkout == null) {
        return new ConfirmClickAction(1, ["Payment form is still loading. Please try again."]);
    }
    else {
        return new ConfirmClickAction(0, [checkout]);
    }
}

export const publishableKey = window.STRIPE_PUBLISHABLE_KEY || '';

/**
 * Check if Stripe is configured (publishable key is non-empty)
 */
export function isConfigured() {
    return !isNullOrWhiteSpace(publishableKey);
}

/**
 * Initialize Stripe instance with the publishable key
 */
export function loadStripe() {
    return window.Stripe(publishableKey);
}

/**
 * Initialize a custom checkout session with an optional Stripe Appearance object.
 * Passing appearance at init time ensures the correct theme is applied before any
 * Payment Elements are created, avoiding a flash of the default (light) theme.
 */
export function initCheckout(stripe, clientSecret, appearance) {
    if (appearance == null) {
        return stripe.initCheckout({fetchClientSecret: function() { return Promise.resolve(clientSecret); }});
    }
    else {
        const a = value(appearance);
        return stripe.initCheckout({fetchClientSecret: function() { return Promise.resolve(clientSecret); }, elementsOptions: {appearance: a}});
    }
}

/**
 * Create a PaymentElement from the checkout instance
 */
export function createPaymentElement(checkout) {
    return checkout.createPaymentElement();
}

/**
 * Mount a PaymentElement into a DOM node
 */
export function mountElement(element, node) {
    element.mount(node);
}

/**
 * Build a Stripe Appearance object that matches the current app theme.
 * Reads the `.dark` class on `<html>` and CSS custom properties to choose
 * the Stripe theme ("night" vs "stripe") and map design-system colors.
 */
export function buildAppearance() {
    return (function() {
            var isDark = document.documentElement.classList.contains('dark');
            var style = getComputedStyle(document.documentElement);
            // Stripe's Appearance API only accepts hex color values — not oklch,
            // rgb, rgba, or var(). Use canvas fillStyle to resolve any CSS color
            // to hex format.
            var ctx = document.createElement('canvas').getContext('2d');
            function toHex(prop) {
                var raw = style.getPropertyValue(prop).trim();
                if (!ctx) return raw;
                ctx.fillStyle = '#000000';
                ctx.fillStyle = raw;
                return ctx.fillStyle;
            }
            return {
                theme: isDark ? 'night' : 'stripe',
                variables: {
                    colorPrimary: toHex('--color-primary-600'),
                    colorDanger: toHex('--color-danger-600'),
                    colorBackground: toHex('--color-surface'),
                    colorText: toHex('--color-neutral-900')
                }
            };
        })();
}

/**
 * Update the appearance of an already-initialized checkout session.
 */
export function changeAppearance(checkout, appearance) {
    checkout.changeAppearance({appearance: appearance});
}

/**
 * Confirm the payment, optionally passing a customer email for Stripe Custom Checkout sessions
 */
export function confirm(email, checkout) {
    if (email == null) {
        return checkout.confirm().then(function(r) { return { Type: r.type || 'success', Error: r.error ? r.error.message : null }; });
    }
    else {
        const e = email;
        return checkout.confirm({email: e}).then(function(r) { return { Type: r.type || 'success', Error: r.error ? r.error.message : null }; });
    }
}

//# sourceMappingURL=Stripe.js.map
